import { useState, useEffect, useRef } from "react";

/** Direction threshold — low enough to feel responsive, high enough to ignore noise */
const SCROLL_DELTA_PX = 3;
/** Only treat as “top” within a few px so the scroll-up look isn’t cleared too early */
const SCROLL_TOP_THRESHOLD_PX = 5;

/**
 * Tracks scroll direction: scrolling up (scrollTop decreasing) yields `true` for a
 * slightly stronger frosted pill; scrolling down or resting near the top yields `false`.
 *
 * @param {React.RefObject<HTMLElement | null>} scrollRef
 * @param {boolean} enabled When false, listener is detached and state resets to false.
 */
export const useBottomToggleScrollTint = (scrollRef, enabled = true) => {
  const [elevated, setElevated] = useState(false);
  const lastScrollTop = useRef(0);

  useEffect(() => {
    if (!enabled) {
      setElevated(false);
      return undefined;
    }

    const el = scrollRef?.current;
    if (!el || typeof el.addEventListener !== "function") {
      return undefined;
    }

    lastScrollTop.current = el.scrollTop;

    const onScroll = () => {
      const top = el.scrollTop;

      if (top <= SCROLL_TOP_THRESHOLD_PX) {
        setElevated(false);
        lastScrollTop.current = top;
        return;
      }

      const delta = top - lastScrollTop.current;
      lastScrollTop.current = top;

      if (delta < -SCROLL_DELTA_PX) {
        setElevated(true);
      } else if (delta > SCROLL_DELTA_PX) {
        setElevated(false);
      }
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
    };
  }, [scrollRef, enabled]);

  return elevated;
};
